import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Panel} from "primereact/panel";
import {Accordion, AccordionTab} from "primereact/accordion";
import {Input} from "@dmt/global_npm";
import ElementLegend from "../../components/chart/ElementLegend";
import {useAppDispatch, useAppSelector} from "../../redux/hooks";
import {DataSource, setControlBarCollapsed} from "../../redux/slices/ChartStateSlice";
import {Dropdown} from "primereact/dropdown";
import {Button} from "primereact/button";
import {InputNumber} from "primereact/inputnumber";

type Props = {
    selectedDrillhole?: any,
    display: number[],
    setDisplay: (value: number[]) => void,
    showLegend?: boolean,
    legendShowAmountColortable?: boolean,
    legendClickable?: boolean,
    legendMultiSelect?: boolean,
    // Steuerung der Legend-Option "Hide inactive" (für Single-Source-Ansicht)
    legendHideInactive?: boolean,
    onLegendHideInactiveChange?: (value: boolean) => void,
    showMenuCard?: boolean,
    menuCardContent?: React.ReactNode,
    scale?: string,
    depths?: { label: string; from: number; to: number }[],
    drillholeOptions?: { value: string; label: string }[],
    selectedDrillholeIds?: string[],
    onDrillholeIdsChange?: (ids: string[]) => void,
    extraControls?: React.ReactNode,
    elementMaxValues?: Record<string, number>,
    onElementMaxChange?: (key: string, value: number | null) => void,
    elementMinValues?: Record<string, number>,
    onElementMinChange?: (key: string, value: number | null) => void,
    combinedChart?: boolean,
    combinedChartCount?: number,
    elementChartIndex?: Record<string, number>,
    onElementChartIndexChange?: (key: string, value: number) => void,
    // Template props
    templates?: any[],
    selectedTemplateId?: string | null,
    onApplyTemplate?: (id: string) => void,
    onSaveTemplate?: () => void,
    onDeleteTemplate?: () => void,
    // PDF export
    onExportPdf?: () => void,
    isExportingPdf?: boolean,
    onScaleChange?: (scale: string) => void,
    onDepthChange?: (from: number, to: number) => void,
    showDepths?: boolean,
    showScale?: boolean,
    // DataSource selection scoped to the owning view. Each view (Chart,
    // CoreViewer, Boreholes3D) passes its own state so the multiselect is
    // independent across views. Optional — only used when showLegend is true.
    dataSources?: DataSource[],
    onDataSourcesChange?: (next: DataSource[]) => void,
    // Per-view active overrides for element visibility. Toggling an element
    // in the legend writes to this map instead of mutating the shared global
    // `active` flag, so hide/show is scoped to the owning view.
    activeOverrides?: Record<string, boolean>,
    onActiveOverridesChange?: (next: Record<string, boolean>) => void,
}
const dataSourceOptions = [
    {value: 'xrf', label: 'XRF'},
    {value: 'minerals', label: 'Minerals'},
];

const SOURCE_LABEL: Record<DataSource, string> = {xrf: 'XRF', minerals: 'Minerals'};

const ControlBar = (props: Props) => {
    const {t} = useTranslation(['common']);
    const dispatch = useAppDispatch();
    const dataSources = props.dataSources ?? [];
    const isCollapsed = useAppSelector((state: any) => state.oldDesignState.isControlBarCollapsed);

    const depthsFrom = props.depths && props.depths.length > 0
        ? props.depths[0].from
        : 0;
    const depthsTo = props.depths && props.depths.length > 0
        ? props.depths[0].to
        : 0;

    // Local input state for From / To. The field the user last committed is
    // "pinned" for a short grace window so the just-typed value isn't
    // immediately overwritten by a slightly rounded extent settling back from
    // the tile viewer. After the grace expires, the next prop change (e.g.
    // the user scrolling/zooming the tile viewer) releases the pin and the
    // field syncs again.
    const PIN_GRACE_MS = 1000;
    const [pinned, setPinned] = useState<'from' | 'to' | null>(null);
    const pinUntilRef = useRef<number>(0);
    const [fromInput, setFromInput] = useState<number>(depthsFrom);
    const [toInput, setToInput] = useState<number>(depthsTo);

    useEffect(() => {
        if (pinned === 'from' && performance.now() < pinUntilRef.current) return;
        setFromInput(depthsFrom);
        if (pinned === 'from') setPinned(null);
    }, [depthsFrom, pinned]);
    useEffect(() => {
        if (pinned === 'to' && performance.now() < pinUntilRef.current) return;
        setToInput(depthsTo);
        if (pinned === 'to') setPinned(null);
    }, [depthsTo, pinned]);

    // Drillhole change → release any pin so inputs reflect the new hole.
    const drillholeKey = props.selectedDrillhole?.hole_id
        ?? (props.selectedDrillholeIds ? props.selectedDrillholeIds.join(',') : null);
    useEffect(() => { setPinned(null); }, [drillholeKey]);

    const commitFrom = (value: number | null | undefined) => {
        if (value == null || !isFinite(value)) return;
        pinUntilRef.current = performance.now() + PIN_GRACE_MS;
        setFromInput(value);
        setPinned('from');
        props.onDepthChange?.(value, toInput);
    };
    const commitTo = (value: number | null | undefined) => {
        if (value == null || !isFinite(value)) return;
        pinUntilRef.current = performance.now() + PIN_GRACE_MS;
        setToInput(value);
        setPinned('to');
        props.onDepthChange?.(fromInput, value);
    };

    const depthsEditable = typeof props.onDepthChange === 'function';

    const toggleCollapsed = () => dispatch(setControlBarCollapsed(!isCollapsed));

    const renderLegend = (source?: DataSource, controlledHideInactive?: boolean) => (
        <ElementLegend
            showAmountColortable={props.legendShowAmountColortable != null ? props.legendShowAmountColortable : true}
            clickable={props.legendClickable != null ? props.legendClickable : true}
            multiSelect={props.legendMultiSelect}
            elementMaxValues={props.elementMaxValues}
            onElementMaxChange={props.onElementMaxChange}
            elementMinValues={props.elementMinValues}
            onElementMinChange={props.onElementMinChange}
            combinedChart={props.combinedChart}
            combinedChartCount={props.combinedChartCount}
            elementChartIndex={props.elementChartIndex}
            onElementChartIndexChange={props.onElementChartIndexChange}
            hideInactive={controlledHideInactive}
            onHideInactiveChange={controlledHideInactive !== undefined ? props.onLegendHideInactiveChange : undefined}
            source={source}
            activeOverrides={props.activeOverrides}
            onActiveOverridesChange={props.onActiveOverridesChange}
        />
    );

    if (isCollapsed) {
        return (
            <div className="h-full flex flex-column align-items-center pt-2"
                 style={{cursor: 'pointer', background: 'var(--surface-card)', borderRight: '1px solid var(--surface-border)', width: '36px'}}
                 onClick={toggleCollapsed}>
                <Button icon="pi pi-chevron-right" className="p-button-text p-button-secondary"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleCollapsed();
                        }}/>
                <span style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    marginTop: '1rem',
                    color: 'var(--text-color-secondary)',
                    fontWeight: 'bold',
                    userSelect: 'none',
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    fontSize: '12px',
                    whiteSpace: 'nowrap'
                }}>
                    Control Bar
                </span>
            </div>
        );
    }

    const header = (
        <div className="flex align-items-center justify-content-between w-full">
            <span className="font-bold">Control Bar</span>
            <Button icon="pi pi-chevron-left" className="p-button-text p-button-secondary p-button-rounded"
                    onClick={toggleCollapsed}/>
        </div>
    );

    return (
        <Panel header={header} className='h-full overflow-auto' style={{zIndex: 9999}}>
            <div className="w-full mb-4">
                {props.templates && (
                    <div className='card overflow-hidden'>
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <Dropdown
                                value={props.selectedTemplateId}
                                options={props.templates}
                                optionLabel="name"
                                optionValue="id"
                                onChange={(e) => props.onApplyTemplate?.(e.value)}
                                placeholder="Templates"
                                className="p-inputtext-sm flex-grow-1"
                                style={{width: '160px'}}
                            />
                            <Button icon="pi pi-plus" className="p-button-sm p-button-outlined flex-shrink-0"
                                    onClick={() => props.onSaveTemplate?.()}/>
                            <Button icon="pi pi-trash"
                                    className="p-button-sm p-button-danger p-button-outlined flex-shrink-0"
                                    onClick={() => props.onDeleteTemplate?.()}
                                    disabled={!props.selectedTemplateId}/>
                        </div>
                    </div>
                )}
                {props.drillholeOptions && props.drillholeOptions.length > 0 &&
                    <div className='card overflow-hidden'>
                        <h4>Drillholes</h4>
                        <Input edit={true} name={'drillholes'}
                               type={'multiselect'}
                               dropdownOptions={props.drillholeOptions}
                               value={props.selectedDrillholeIds ?? []}
                               onChange={(e: any) => {
                                   props.onDrillholeIdsChange?.(e.value ?? []);
                               }}/>
                    </div>}
                {props.onExportPdf && (
                    <div className='card overflow-hidden'>
                        <Button
                            label={props.isExportingPdf ? "Exporting..." : "PDF Export"}
                            icon={props.isExportingPdf ? "pi pi-spin pi-spinner" : "pi pi-file-pdf"}
                            className="p-button-sm w-full"
                            onClick={props.onExportPdf}
                            disabled={props.isExportingPdf}
                        />
                    </div>
                )}
                {props.showMenuCard &&
                    <div className='card overflow-hidden'>
                        {props.menuCardContent}
                    </div>}
                {((props.showScale ?? true) || (props.showDepths ?? true)) &&
                    <div className='card overflow-hidden'>
                        {(props.showScale ?? true) && <>
                            <h4>Scale</h4>
                            <div style={{padding: '4px 0'}}>{props.scale ?? '—'}</div>
                        </>}
                        {(props.showDepths ?? true) && <>
                            <h4 className={(props.showScale ?? true) ? 'mt-3' : ''}>Depths</h4>
                            <div className='grid'>
                                <div className='col-4 my-auto'><b>From</b></div>
                                <div className='col-8'>
                                    {depthsEditable ? (
                                        <InputNumber
                                            value={fromInput}
                                            onValueChange={(e) => commitFrom(e.value ?? null)}
                                            maxFractionDigits={2}
                                            suffix=" m"
                                        />
                                    ) : (
                                        <div style={{padding: '4px 0'}}>{depthsFrom.toFixed(2)} m</div>
                                    )}
                                </div>
                                <div className='col-4 my-auto'><b>To</b></div>
                                <div className='col-8'>
                                    {depthsEditable ? (
                                        <InputNumber
                                            value={toInput}
                                            onValueChange={(e) => commitTo(e.value ?? null)}
                                            maxFractionDigits={2}
                                            suffix=" m"
                                        />
                                    ) : (
                                        <div style={{padding: '4px 0'}}>{depthsTo.toFixed(2)} m</div>
                                    )}
                                </div>
                            </div>
                        </>}
                    </div>
                }
                {props.extraControls &&
                    <div className='card overflow-hidden'>
                        {props.extraControls}
                    </div>}

                {props.showLegend &&
                    <div className='card overflow-hidden'>
                        <h4>Data Source</h4>
                        <Input edit={true} name={'dataSources'} type={'multiselect'}
                               value={dataSources ?? []}
                               onChange={(e: any) => {
                                   const next = (e.value ?? []) as DataSource[];
                                   props.onDataSourcesChange?.(next);
                               }}
                               dropdownOptions={dataSourceOptions}/>
                        {dataSources.length === 0 ? null : dataSources.length <= 1 ? (
                            renderLegend(dataSources[0] ?? 'xrf', props.legendHideInactive)
                        ) : (
                            <Accordion multiple activeIndex={dataSources.map((_, i) => i)}>
                                {dataSources.map((src) => (
                                    <AccordionTab key={src} header={SOURCE_LABEL[src]}>
                                        {renderLegend(src)}
                                    </AccordionTab>
                                ))}
                            </Accordion>
                        )}
                    </div>}
            </div>
        </Panel>);
};

export default ControlBar;
